import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import type { IAdicionalRepository } from '@/domain/repositories/IAdicionalRepository';
import type { ITenantAdicionalRepository, TenantExtrasSnapshot } from '@/domain/repositories/ITenantAdicionalRepository';
import type { TenantAdicionalDTO } from '@/domain/entities/TenantAdicional';
import { Adicional } from '@/domain/entities/Adicional';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input } from '@/presentation/components/common';
import {
  createWhatsAppBonus,
  listWhatsAppBonuses,
  revokeWhatsAppBonus,
  type WhatsAppBonusDTO,
} from '@/infrastructure/api/whatsappBonusApi';
import {
  createEmailMarketingBonus,
  listEmailMarketingBonuses,
  revokeEmailMarketingBonus,
  type EmailMarketingBonusDTO,
} from '@/infrastructure/api/emailMarketingBonusApi';
import {
  listPendingPlanChanges,
  schedulePlanChange,
  cancelPlanChange,
  type PlanChangeDTO,
} from '@/infrastructure/api/planChangeApi';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type ProductKey = 'conexion' | 'marketing' | 'bot';
type DetailTab = 'escalon' | 'bonos' | 'trial';

/** Excedentes de la clínica: solo lectura, los administra la propia clínica. */
type OverageStatus = {
  enabled: boolean;
  maxMessages: number | null;
  period: string;
  overageMessages: number;
  pricePerMessage: number;
  amount: number;
  status: 'open' | 'billed' | null;
};
type WaScope = 'all' | 'manual_only';

export type CommercialChangeLine = {
  label: string;
  value: string;
};

export type TenantCommercialPacksHandle = {
  hasChanges: () => boolean;
  summarize: () => CommercialChangeLine[];
  validate: () => string | null;
  commit: () => Promise<void>;
};

type Props = {
  tenantId: string | number;
  tenantName?: string;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
};

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

function errMsg(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax.response?.data?.message || (err instanceof Error ? err.message : fallback);
}

function currentUtcPeriod(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function ExtraStepper({
  label,
  included,
  value,
  used,
  min = 0,
  unitPrice,
  disabled,
  onChange,
}: {
  label: string;
  included: number;
  value: number;
  used: number | null;
  min?: number;
  unitPrice: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  const contracted = included + value;
  const floor = Math.max(0, min);
  return (
    <div className="rounded-md border border-slate-200 px-3 py-2 dark:border-gray-700">
      <p className="text-xs font-semibold text-gray-900 dark:text-white">{label}</p>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || value <= floor}
          onClick={() => onChange(Math.max(floor, value - 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-slate-200"
          aria-label={`Quitar ${label}`}
        >
          −
        </button>
        <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
          {value}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(value + 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-slate-200"
          aria-label={`Agregar ${label}`}
        >
          +
        </button>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {money(unitPrice * value)}/mes
        </span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        Contratados: {contracted} ({included} del plan + {value} extra)
        {used != null ? ` · En Coofit: ${used} activos` : ''}
      </p>
    </div>
  );
}

function EnableSwitch({
  on,
  disabled,
  onChange,
  onLabel,
  offLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  onLabel: string;
  offLabel: string;
}) {
  return (
    <label className="inline-flex items-center gap-2">
      <span
        className={`text-xs font-semibold ${
          on ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'
        }`}
      >
        {on ? onLabel : offLabel}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          on ? 'bg-teal-600' : 'bg-slate-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`mt-0.5 inline-block h-5 w-5 rounded-full bg-white transition-transform ${
            on ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function shortTierLabel(kind: 'conexion' | 'marketing', cupo: number | null | undefined): string {
  const n = cupo != null && cupo > 0 ? cupo.toLocaleString('es-AR') : '—';
  return kind === 'conexion' ? `Conexión ${n}` : `Marketing ${n}`;
}

function scopeLabel(scope: WaScope): string {
  return scope === 'manual_only' ? 'solo envíos manuales' : 'automáticos + manuales';
}

function fechaEfectivaLabel(fechaEfectiva: string): string {
  const [y, m, d] = fechaEfectiva.split('-').map(Number);
  if (!y || !m || !d) return fechaEfectiva;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

type TierSelectProps = {
  kind: 'conexion' | 'marketing';
  volumeHeader: string;
  catalog: Adicional[];
  selectedId: number | null;
  readOnly: boolean;
  onSelect: (id: number | null) => void;
};

const TIER_SELECT_CLASS =
  'block w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:cursor-not-allowed disabled:opacity-60';

const TierSelect: React.FC<TierSelectProps> = ({
  kind,
  volumeHeader,
  catalog,
  selectedId,
  readOnly,
  onSelect,
}) => {
  const unit = kind === 'conexion' ? 'msgs' : 'emails';
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
        {volumeHeader}
      </label>
      <select
        value={selectedId ?? ''}
        disabled={readOnly}
        onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
        className={TIER_SELECT_CLASS}
      >
        <option value="">Elegí un escalón…</option>
        {catalog.map((adicional) => {
          const cupo = adicional.cupoMensajes ?? 0;
          return (
            <option key={adicional.id} value={adicional.id}>
              {shortTierLabel(kind, cupo)} ·{' '}
              {cupo > 0 ? `${cupo.toLocaleString('es-AR')} ${unit}/mes` : 'sin cupo'} ·{' '}
              {money(adicional.precioUnitario)}/mes
            </option>
          );
        })}
      </select>
    </div>
  );
};

function resolveLabelForValue(
  kind: 'conexion' | 'marketing' | 'bot',
  catalog: Adicional[],
  id: number | null,
): string {
  if (id == null) return 'Apagado';
  const item = catalog.find((a) => a.id === id);
  if (!item) return `#${id}`;
  return kind === 'bot' ? item.nombre : shortTierLabel(kind, item.cupoMensajes);
}

const PendingChangeBanner: React.FC<{
  pending: PlanChangeDTO;
  catalog: Adicional[];
  kind: 'conexion' | 'marketing' | 'bot';
  onCancel: () => void;
  canceling: boolean;
}> = ({ pending, catalog, kind, onCancel, canceling }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
    <span>
      Cambio programado: pasa a <strong>{resolveLabelForValue(kind, catalog, pending.valorNuevoId)}</strong>{' '}
      el {fechaEfectivaLabel(pending.fechaEfectiva)}. Hasta entonces sigue el valor vigente.
    </span>
    <button
      type="button"
      onClick={onCancel}
      disabled={canceling}
      className="shrink-0 font-semibold text-amber-900 underline hover:no-underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-100"
    >
      {canceling ? 'Cancelando…' : 'Cancelar cambio programado'}
    </button>
  </div>
);

const ForceImmediateControl: React.FC<{
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  reason: string;
  onReasonChange: (next: string) => void;
}> = ({ checked, onCheckedChange, reason, onReasonChange }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
    <p className="text-xs text-slate-500 dark:text-slate-400">
      Por defecto este cambio rige recién el 1° del próximo mes — este mes se sigue facturando y
      midiendo cupo con el valor actual.
    </p>
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
      />
      Aplicar ahora (excepcional)
    </label>
    {checked ? (
      <Input
        label="Motivo (auditoría)"
        type="text"
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        placeholder="Ej. la clínica necesita el cambio urgente"
      />
    ) : null}
  </div>
);

export const TenantCommercialPacksSection = forwardRef<TenantCommercialPacksHandle, Props>(
  function TenantCommercialPacksSection({ tenantId, readOnly = false, onDirtyChange }, ref) {
    const { error: showError, success: showSuccess } = useToast();
    const [catalog, setCatalog] = useState<Adicional[]>([]);
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState<ProductKey>('conexion');
    const [detailTab, setDetailTab] = useState<DetailTab>('escalon');

    const [savedConexionId, setSavedConexionId] = useState<number | null>(null);
    const [savedMarketingId, setSavedMarketingId] = useState<number | null>(null);
    const [savedBotId, setSavedBotId] = useState<number | null>(null);
    const [overage, setOverage] = useState<OverageStatus | null>(null);
    const [draftConexionId, setDraftConexionId] = useState<number | null>(null);
    const [draftMarketingId, setDraftMarketingId] = useState<number | null>(null);
    const [draftBotId, setDraftBotId] = useState<number | null>(null);
    const [draftConexionOn, setDraftConexionOn] = useState(false);
    const [draftMarketingOn, setDraftMarketingOn] = useState(false);
    const [draftBotOn, setDraftBotOn] = useState(false);

    const [pendingChanges, setPendingChanges] = useState<PlanChangeDTO[]>([]);
    const [forceConexion, setForceConexion] = useState(false);
    const [forceConexionReason, setForceConexionReason] = useState('');
    const [forceMarketing, setForceMarketing] = useState(false);
    const [forceMarketingReason, setForceMarketingReason] = useState('');
    const [forceBot, setForceBot] = useState(false);
    const [forceBotReason, setForceBotReason] = useState('');
    const [cancelingChangeId, setCancelingChangeId] = useState<number | null>(null);

    const [savedConsultorioExtra, setSavedConsultorioExtra] = useState(0);
    const [savedProfesionalExtra, setSavedProfesionalExtra] = useState(0);
    const [draftConsultorioExtra, setDraftConsultorioExtra] = useState(0);
    const [draftProfesionalExtra, setDraftProfesionalExtra] = useState(0);
    const [extrasSnapshot, setExtrasSnapshot] = useState<TenantExtrasSnapshot | null>(null);

    const [includeWaRecharge, setIncludeWaRecharge] = useState(false);
    const [rechargeMessages, setRechargeMessages] = useState(100);
    const [rechargeReason, setRechargeReason] = useState('');
    const [includeWaBonus, setIncludeWaBonus] = useState(false);
    const [waMessages, setWaMessages] = useState(50);
    const [waScope, setWaScope] = useState<WaScope>('manual_only');
    const [waReason, setWaReason] = useState('Trial comercial — envíos manuales');
    const [includeMktBonus, setIncludeMktBonus] = useState(false);
    const [mktMessages, setMktMessages] = useState(50);
    const [mktReason, setMktReason] = useState('Trial comercial Marketing');
    const [revokeWaIds, setRevokeWaIds] = useState<number[]>([]);
    const [revokeMktIds, setRevokeMktIds] = useState<number[]>([]);
    const [waHistory, setWaHistory] = useState<WhatsAppBonusDTO[]>([]);
    const [mktHistory, setMktHistory] = useState<EmailMarketingBonusDTO[]>([]);
    const lastConexionId = React.useRef<number | null>(null);
    const lastMarketingId = React.useRef<number | null>(null);
    const lastBotId = React.useRef<number | null>(null);

    const tid = String(tenantId);
    const period = currentUtcPeriod();

    const hydrateFromAssigned = useCallback(
      (list: TenantAdicionalDTO[], items: Adicional[], snapshot: TenantExtrasSnapshot | null) => {
        const active = new Set(list.filter((r) => r.activo).map((r) => r.adicionalAdminId));
        const conexion =
          items.find((a) => a.isCoofitConexion() && active.has(a.id))?.id ?? null;
        const marketing =
          items.find((a) => a.isCoofitMarketing() && active.has(a.id))?.id ?? null;
        const bot = items.find((a) => a.isCoofitWhatsAppBot() && active.has(a.id))?.id ?? null;
        setSavedConexionId(conexion);
        setSavedMarketingId(marketing);
        setSavedBotId(bot);
        setDraftConexionId(conexion);
        setDraftMarketingId(marketing);
        setDraftBotId(bot);
        setDraftConexionOn(conexion != null);
        setDraftMarketingOn(marketing != null);
        setDraftBotOn(bot != null);
        lastConexionId.current = conexion;
        lastMarketingId.current = marketing;
        lastBotId.current = bot;
        const consSku = items.find((a) => a.isConsultorioExtra());
        const profSku = items.find((a) => a.isProfesionalExtra());
        const consQty =
          consSku != null
            ? list.find((r) => r.activo && r.adicionalAdminId === consSku.id)?.cantidad ?? 0
            : 0;
        const profQty =
          profSku != null
            ? list.find((r) => r.activo && r.adicionalAdminId === profSku.id)?.cantidad ?? 0
            : 0;
        const consStart = readOnly
          ? consQty
          : Math.max(
              consQty,
              snapshot?.usageAvailable ? snapshot.inferredExtraConsultorios : 0,
            );
        const profStart = readOnly
          ? profQty
          : Math.max(
              profQty,
              snapshot?.usageAvailable ? snapshot.inferredExtraProfesionales : 0,
            );
        setSavedConsultorioExtra(consQty);
        setSavedProfesionalExtra(profQty);
        setDraftConsultorioExtra(consStart);
        setDraftProfesionalExtra(profStart);
        setIncludeWaRecharge(false);
        setRechargeMessages(100);
        setRechargeReason('');
        setIncludeWaBonus(false);
        setIncludeMktBonus(false);
        setRevokeWaIds([]);
        setRevokeMktIds([]);
        setWaMessages(50);
        setWaScope('manual_only');
        setWaReason('Trial comercial — envíos manuales');
        setMktMessages(50);
        setMktReason('Trial comercial Marketing');
        setForceConexion(false);
        setForceConexionReason('');
        setForceMarketing(false);
        setForceMarketingReason('');
        setForceBot(false);
        setForceBotReason('');
      },
      [readOnly],
    );

    const reload = useCallback(async () => {
      setLoading(true);
      try {
        const adicionalRepo = container.get<IAdicionalRepository>(TYPES.IAdicionalRepository);
        const tenantAdRepo = container.get<ITenantAdicionalRepository>(
          TYPES.ITenantAdicionalRepository,
        );
        const [allActive, listRaw, overageRes, wa, mkt, pending, snapshotRaw] = await Promise.all([
          adicionalRepo.getAll(undefined, true),
          tenantAdRepo.getByTenantId(tid),
          httpClient
            .get<OverageStatus>(`/admin/tenants/${tid}/whatsapp-overage`)
            .catch(() => null),
          listWhatsAppBonuses(tid, { period, includeRevoked: false }).catch(() => []),
          listEmailMarketingBonuses(tid, { period, includeRevoked: false }).catch(() => []),
          listPendingPlanChanges(tid).catch(() => []),
          tenantAdRepo.getExtrasSnapshot(tid).catch(() => null),
        ]);
        let list = listRaw;
        let snapshot = snapshotRaw;
        if (
          !readOnly
          && snapshot?.usageAvailable
          && (snapshot.inferredExtraConsultorios > snapshot.extraConsultorios
            || snapshot.inferredExtraProfesionales > snapshot.extraProfesionales)
        ) {
          const before = snapshot;
          snapshot = await tenantAdRepo.syncExtrasFromUsage(tid);
          list = await tenantAdRepo.getByTenantId(tid);
          if (
            snapshot.extraConsultorios > before.extraConsultorios
            || snapshot.extraProfesionales > before.extraProfesionales
          ) {
            showSuccess(
              'Extras alineados al uso real de Coofit. El extra nuevo se cobra en la próxima factura.',
            );
          }
        }
        const items = allActive.filter((a) => a.isActive());
        setOverage(overageRes?.data ?? null);
        setCatalog(items);
        setWaHistory(wa);
        setMktHistory(mkt);
        setPendingChanges(pending);
        setExtrasSnapshot(snapshot);
        hydrateFromAssigned(list, items, snapshot);
      } catch (err) {
        showError(errMsg(err, 'No se pudo cargar el catálogo comercial'));
      } finally {
        setLoading(false);
      }
    }, [tid, period, showError, showSuccess, hydrateFromAssigned, readOnly]);

    const pendingConexion = useMemo(
      () => pendingChanges.find((p) => p.tipo === 'COOFIT_CONEXION') ?? null,
      [pendingChanges],
    );
    const pendingMarketing = useMemo(
      () => pendingChanges.find((p) => p.tipo === 'COOFIT_MARKETING') ?? null,
      [pendingChanges],
    );
    const pendingBot = useMemo(
      () => pendingChanges.find((p) => p.tipo === 'COOFIT_WHATSAPP_BOT') ?? null,
      [pendingChanges],
    );

    const cancelPending = useCallback(
      async (change: PlanChangeDTO) => {
        setCancelingChangeId(change.id);
        try {
          await cancelPlanChange(tid, change.id);
          await reload();
        } catch (err) {
          showError(errMsg(err, 'No se pudo cancelar el cambio programado'));
        } finally {
          setCancelingChangeId(null);
        }
      },
      [tid, reload, showError],
    );

    useEffect(() => {
      void reload();
    }, [reload]);

    const conexionCatalog = useMemo(
      () =>
        catalog
          .filter((a) => a.isCoofitConexion())
          .slice()
          .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0)),
      [catalog],
    );
    const marketingCatalog = useMemo(
      () =>
        catalog
          .filter((a) => a.isCoofitMarketing())
          .slice()
          .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0)),
      [catalog],
    );

    const botCatalog = useMemo(
      () =>
        catalog
          .filter((a) => a.isCoofitWhatsAppBot())
          .slice()
          .sort((a, b) => a.precioUnitario - b.precioUnitario),
      [catalog],
    );

    const consultorioExtraSku = useMemo(
      () => catalog.find((a) => a.isConsultorioExtra()) ?? null,
      [catalog],
    );
    const profesionalExtraSku = useMemo(
      () => catalog.find((a) => a.isProfesionalExtra()) ?? null,
      [catalog],
    );

    const savedConexion = conexionCatalog.find((a) => a.id === savedConexionId) ?? null;
    const draftConexion = conexionCatalog.find((a) => a.id === draftConexionId) ?? null;
    const savedMarketing = marketingCatalog.find((a) => a.id === savedMarketingId) ?? null;
    const draftMarketing = marketingCatalog.find((a) => a.id === draftMarketingId) ?? null;
    const draftBot = botCatalog.find((a) => a.id === draftBotId) ?? null;

    const summarize = useCallback((): CommercialChangeLine[] => {
      const lines: CommercialChangeLine[] = [];
      if (draftConsultorioExtra !== savedConsultorioExtra) {
        lines.push({
          label: 'Consultorios extra',
          value: `${savedConsultorioExtra} → ${draftConsultorioExtra} (inmediato · ${money(
            (consultorioExtraSku?.precioUnitario ?? 15000) * draftConsultorioExtra,
          )}/mes)`,
        });
      }
      if (draftProfesionalExtra !== savedProfesionalExtra) {
        lines.push({
          label: 'Profesionales extra',
          value: `${savedProfesionalExtra} → ${draftProfesionalExtra} (inmediato · ${money(
            (profesionalExtraSku?.precioUnitario ?? 15000) * draftProfesionalExtra,
          )}/mes)`,
        });
      }
      const savedConexionOn = savedConexionId != null;
      const savedMarketingOn = savedMarketingId != null;
      const timingSuffix = (wasActive: boolean, forced: boolean): string => {
        if (!wasActive) return ' (inmediato · alta)';
        if (forced) return ' (inmediato · forzado)';
        return ' (rige desde el próximo período)';
      };
      if (draftConexionOn !== savedConexionOn || draftConexionId !== savedConexionId) {
        const from = savedConexion
          ? `Prendido · ${shortTierLabel('conexion', savedConexion.cupoMensajes)}`
          : 'Apagado';
        const to = !draftConexionOn
          ? 'Apagado'
          : draftConexion
            ? `Prendido · ${shortTierLabel('conexion', draftConexion.cupoMensajes)}`
            : 'Prendido · sin escalón';
        lines.push({
          label: 'Coofit Conexión',
          value: `${from} → ${to}${timingSuffix(savedConexionOn, forceConexion)}`,
        });
      }
      if (draftMarketingOn !== savedMarketingOn || draftMarketingId !== savedMarketingId) {
        const from = savedMarketing
          ? `Prendido · ${shortTierLabel('marketing', savedMarketing.cupoMensajes)}`
          : 'Apagado';
        const to = !draftMarketingOn
          ? 'Apagado'
          : draftMarketing
            ? `Prendido · ${shortTierLabel('marketing', draftMarketing.cupoMensajes)}`
            : 'Prendido · sin escalón';
        lines.push({
          label: 'Coofit Marketing',
          value: `${from} → ${to}${timingSuffix(savedMarketingOn, forceMarketing)}`,
        });
      }
      if (draftBotOn !== (savedBotId != null) || draftBotId !== savedBotId) {
        lines.push({
          label: 'Agenda WhatsApp (add-on Conexión)',
          value: `${savedBotId != null ? 'Prendido' : 'Apagado'} → ${
            draftBotOn ? 'Prendido' : 'Apagado'
          }${timingSuffix(savedBotId != null, forceBot)}`,
        });
      }
      if (includeWaRecharge && rechargeMessages > 0) {
        lines.push({
          label: `Recarga Conexión (${period})`,
          value: `+${rechargeMessages.toLocaleString('es-AR')} msgs · automáticos + manuales · ${rechargeReason.trim()}`,
        });
      }
      if (includeWaBonus && waMessages > 0) {
        lines.push({
          label: 'Bonificación / trial Conexión',
          value: `+${waMessages.toLocaleString('es-AR')} msgs · ${scopeLabel(waScope)} · ${waReason.trim()}`,
        });
      }
      if (includeMktBonus && mktMessages > 0) {
        lines.push({
          label: 'Bonificación Marketing',
          value: `+${mktMessages.toLocaleString('es-AR')} emails · ${mktReason.trim()}`,
        });
      }
      for (const id of revokeWaIds) {
        const b = waHistory.find((row) => row.id === id);
        if (b) {
          lines.push({
            label: 'Anular bono Conexión',
            value: `+${b.messages.toLocaleString('es-AR')} msgs (${scopeLabel(b.scope)})`,
          });
        }
      }
      for (const id of revokeMktIds) {
        const b = mktHistory.find((row) => row.id === id);
        if (b) {
          lines.push({
            label: 'Anular bono Marketing',
            value: `+${b.messages.toLocaleString('es-AR')} emails`,
          });
        }
      }
      return lines;
    }, [
      draftConexionOn,
      draftConexionId,
      savedConexionId,
      savedConexion,
      draftConexion,
      draftMarketingOn,
      draftMarketingId,
      savedMarketingId,
      savedMarketing,
      draftMarketing,
      draftBotOn,
      draftBotId,
      savedBotId,
      forceConexion,
      forceMarketing,
      forceBot,
      includeWaRecharge,
      rechargeMessages,
      rechargeReason,
      period,
      includeWaBonus,
      waMessages,
      waScope,
      waReason,
      includeMktBonus,
      mktMessages,
      mktReason,
      revokeWaIds,
      revokeMktIds,
      waHistory,
      mktHistory,
      draftConsultorioExtra,
      savedConsultorioExtra,
      draftProfesionalExtra,
      savedProfesionalExtra,
      consultorioExtraSku,
      profesionalExtraSku,
    ]);

    const validate = useCallback((): string | null => {
      if (draftConexionOn && draftConexionId == null) {
        return 'Coofit Conexión está prendido: elegí un escalón';
      }
      if (draftMarketingOn && draftMarketingId == null) {
        return 'Coofit Marketing está prendido: elegí un escalón';
      }
      if (draftBotOn && !draftConexionOn) {
        return 'Agenda WhatsApp es un add-on de Coofit Conexión. Primero prendé Conexión.';
      }
      if (draftBotOn && draftBotId == null) {
        return 'Coofit Agenda WhatsApp está prendido: elegí un pack';
      }
      if (forceConexion && forceConexionReason.trim().length < 5) {
        return 'Aplicar ahora Conexión: indicá un motivo (mínimo 5 caracteres)';
      }
      if (forceMarketing && forceMarketingReason.trim().length < 5) {
        return 'Aplicar ahora Marketing: indicá un motivo (mínimo 5 caracteres)';
      }
      if (forceBot && forceBotReason.trim().length < 5) {
        return 'Aplicar ahora Agenda WhatsApp: indicá un motivo (mínimo 5 caracteres)';
      }
      if (includeWaRecharge) {
        if (!draftConexionOn || draftConexionId == null) {
          return 'Prendé Coofit Conexión y elegí un escalón para recargar';
        }
        if (!Number.isFinite(rechargeMessages) || rechargeMessages < 1) {
          return 'Indicá mensajes de recarga mayores a 0';
        }
        if (rechargeReason.trim().length < 5) {
          return 'Motivo de recarga: mínimo 5 caracteres (auditoría)';
        }
        if (rechargeReason.trim().length > 500) {
          return 'Motivo de recarga: máximo 500 caracteres';
        }
        if (rechargeMessages > 100_000) {
          return 'La recarga no puede superar 100.000 mensajes';
        }
      }
      if (includeWaBonus) {
        if (!draftConexionOn || draftConexionId == null) {
          return 'Prendé Coofit Conexión y elegí un escalón para el trial';
        }
        if (!Number.isFinite(waMessages) || waMessages < 1) {
          return 'Indicá mensajes WhatsApp mayores a 0';
        }
        if (waReason.trim().length < 5) {
          return 'Motivo WhatsApp: mínimo 5 caracteres';
        }
      }
      if (includeMktBonus) {
        if (!draftMarketingOn || draftMarketingId == null) {
          return 'Prendé Coofit Marketing y elegí un escalón para el trial';
        }
        if (!Number.isFinite(mktMessages) || mktMessages < 1) {
          return 'Indicá emails mayores a 0';
        }
        if (mktReason.trim().length < 5) {
          return 'Motivo Marketing: mínimo 5 caracteres';
        }
      }
      return null;
    }, [
      draftConexionOn,
      draftConexionId,
      draftMarketingOn,
      draftMarketingId,
      draftBotOn,
      draftBotId,
      forceConexion,
      forceConexionReason,
      forceMarketing,
      forceMarketingReason,
      forceBot,
      forceBotReason,
      includeWaRecharge,
      rechargeMessages,
      rechargeReason,
      includeWaBonus,
      waMessages,
      waReason,
      includeMktBonus,
      mktMessages,
      mktReason,
    ]);

    const commit = useCallback(async () => {
      const invalid = validate();
      if (invalid) throw new Error(invalid);

      if (
        draftConsultorioExtra !== savedConsultorioExtra ||
        draftProfesionalExtra !== savedProfesionalExtra
      ) {
        const tenantAdRepo = container.get<ITenantAdicionalRepository>(
          TYPES.ITenantAdicionalRepository,
        );
        await tenantAdRepo.setExtras(tid, {
          consultoriosExtra: draftConsultorioExtra,
          profesionalesExtra: draftProfesionalExtra,
        });
      }

      const savedConexionOn = savedConexionId != null;
      if (draftConexionOn !== savedConexionOn || draftConexionId !== savedConexionId) {
        await schedulePlanChange(tid, {
          tipo: 'COOFIT_CONEXION',
          valorNuevoId: draftConexionOn ? draftConexionId : null,
          forzarInmediato: forceConexion,
          motivo: forceConexion ? forceConexionReason.trim() : undefined,
        });
      }
      const savedMarketingOn = savedMarketingId != null;
      if (draftMarketingOn !== savedMarketingOn || draftMarketingId !== savedMarketingId) {
        await schedulePlanChange(tid, {
          tipo: 'COOFIT_MARKETING',
          valorNuevoId: draftMarketingOn ? draftMarketingId : null,
          forzarInmediato: forceMarketing,
          motivo: forceMarketing ? forceMarketingReason.trim() : undefined,
        });
      }
      const savedBotOn = savedBotId != null;
      if (draftBotOn !== savedBotOn || draftBotId !== savedBotId) {
        await schedulePlanChange(tid, {
          tipo: 'COOFIT_WHATSAPP_BOT',
          valorNuevoId: draftBotOn ? draftBotId : null,
          forzarInmediato: forceBot,
          motivo: forceBot ? forceBotReason.trim() : undefined,
        });
      }

      if (includeWaRecharge) {
        await createWhatsAppBonus(tid, {
          messages: Math.floor(rechargeMessages),
          reason: `Recarga manual: ${rechargeReason.trim()}`,
          period,
          scope: 'all',
        });
      }

      if (includeWaBonus) {
        await createWhatsAppBonus(tid, {
          messages: Math.floor(waMessages),
          reason: waReason.trim(),
          period,
          scope: waScope,
        });
      }
      if (includeMktBonus) {
        await createEmailMarketingBonus(tid, {
          messages: Math.floor(mktMessages),
          reason: mktReason.trim(),
          period,
        });
      }
      for (const id of revokeWaIds) {
        await revokeWhatsAppBonus(id, { revokeReason: 'Anulado desde ficha clínica' });
      }
      for (const id of revokeMktIds) {
        await revokeEmailMarketingBonus(id, { revokeReason: 'Anulado desde ficha clínica' });
      }
      await reload();
    }, [
      validate,
      tid,
      savedConexionId,
      savedMarketingId,
      savedBotId,
      draftConexionOn,
      draftConexionId,
      draftMarketingOn,
      draftMarketingId,
      draftBotOn,
      draftBotId,
      forceConexion,
      forceConexionReason,
      forceMarketing,
      forceMarketingReason,
      forceBot,
      forceBotReason,
      includeWaRecharge,
      rechargeMessages,
      rechargeReason,
      period,
      includeWaBonus,
      waMessages,
      waReason,
      waScope,
      includeMktBonus,
      mktMessages,
      mktReason,
      revokeWaIds,
      revokeMktIds,
      reload,
      draftConsultorioExtra,
      savedConsultorioExtra,
      draftProfesionalExtra,
      savedProfesionalExtra,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        hasChanges: () => summarize().length > 0,
        summarize,
        validate,
        commit,
      }),
      [summarize, validate, commit],
    );

    useEffect(() => {
      onDirtyChange?.(summarize().length > 0);
    }, [summarize, onDirtyChange]);

    if (loading) {
      return (
        <div className="flex h-full min-h-[28rem] items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 dark:border-gray-600">
          Cargando…
        </div>
      );
    }

    const fieldClass =
      'block w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

    const productBtn = (key: ProductKey, title: string, subtitle: string, dirty: boolean) => (
      <button
        type="button"
        onClick={() => {
          setProduct(key);
          if (key !== 'conexion' && detailTab === 'bonos') setDetailTab('escalon');
          if (key === 'bot') setDetailTab('escalon');
        }}
        className={`w-full rounded-lg border px-3 py-3 text-left transition ${
          product === key
            ? 'border-teal-500 bg-teal-50/70 dark:border-teal-500 dark:bg-teal-950/30'
            : 'border-slate-200 hover:border-slate-300 dark:border-gray-700 dark:hover:border-gray-600'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
          {dirty ? (
            <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-300">
              Pendiente
            </span>
          ) : null}
        </div>
        <p className="mt-1 line-clamp-2 min-h-[2rem] text-xs text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      </button>
    );

    const waEscalonDirty =
      draftConexionOn !== (savedConexionId != null) ||
      draftConexionId !== savedConexionId;
    const waBonosDirty = includeWaRecharge;
    const waTrialDirty = includeWaBonus || revokeWaIds.length > 0;
    const mktEscalonDirty =
      draftMarketingOn !== (savedMarketingId != null) || draftMarketingId !== savedMarketingId;
    const mktTrialDirty = includeMktBonus || revokeMktIds.length > 0;
    const botDirty = draftBotOn !== (savedBotId != null) || draftBotId !== savedBotId;
    const waDirty = waEscalonDirty || waBonosDirty || waTrialDirty;
    const mktDirty = mktEscalonDirty || mktTrialDirty;
    const trialDirty = product === 'conexion' ? waTrialDirty : mktTrialDirty;
    const escalonDirty =
      product === 'conexion' ? waEscalonDirty : product === 'marketing' ? mktEscalonDirty : botDirty;

    const productOn =
      product === 'conexion' ? draftConexionOn : product === 'marketing' ? draftMarketingOn : draftBotOn;
    const productReady =
      product === 'conexion'
        ? draftConexionOn && draftConexionId != null
        : product === 'marketing'
          ? draftMarketingOn && draftMarketingId != null
          : draftBotOn && draftBotId != null;

    const setProductEnabled = (on: boolean) => {
      if (product === 'bot') {
        if (on) {
          setDraftBotOn(true);
          // Pack único: no tiene sentido pedir que elijan si hay uno solo.
          setDraftBotId((cur) => cur ?? lastBotId.current ?? botCatalog[0]?.id ?? null);
        } else {
          if (draftBotId != null) lastBotId.current = draftBotId;
          setDraftBotOn(false);
          setDraftBotId(null);
        }
        return;
      }
      if (product === 'conexion') {
        if (on) {
          setDraftConexionOn(true);
          setDraftConexionId((cur) => cur ?? lastConexionId.current);
        } else {
          if (draftConexionId != null) lastConexionId.current = draftConexionId;
          setDraftConexionOn(false);
          setDraftConexionId(null);
          setIncludeWaRecharge(false);
          setIncludeWaBonus(false);
          if (draftBotId != null) lastBotId.current = draftBotId;
          setDraftBotOn(false);
          setDraftBotId(null);
        }
        return;
      }
      if (on) {
        setDraftMarketingOn(true);
        setDraftMarketingId((cur) => cur ?? lastMarketingId.current);
      } else {
        if (draftMarketingId != null) lastMarketingId.current = draftMarketingId;
        setDraftMarketingOn(false);
        setDraftMarketingId(null);
        setIncludeMktBonus(false);
      }
    };

    const subtabClass = (tab: DetailTab) =>
      `inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        detailTab === tab
          ? 'border-teal-500 text-teal-600 dark:text-teal-400'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
      }`;

    return (
      <div className="flex h-full min-h-[28rem] flex-col gap-4">
        <div className="rounded-lg border border-slate-200 p-3 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Consultorios y profesionales extra
              </h4>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                El plan incluye {extrasSnapshot?.includedConsultorios ?? 1} consultorio y{' '}
                {extrasSnapshot?.includedProfesionales ?? 1} profesional. Cada extra suma{' '}
                {money(consultorioExtraSku?.precioUnitario ?? profesionalExtraSku?.precioUnitario ?? 15000)}
                /mes.{' '}
                {readOnly
                  ? 'Se muestra lo contratado frente a lo que la clínica ya tiene en Coofit.'
                  : 'Al abrir se parte de los consultorios y profesionales activos. El extra nuevo se cobra en la próxima factura. Un ajuste a mano cobra ya (prorrateo).'}
              </p>
            </div>
            {draftConsultorioExtra !== savedConsultorioExtra ||
            draftProfesionalExtra !== savedProfesionalExtra ? (
              <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-300">
                Pendiente
              </span>
            ) : null}
          </div>
          {!consultorioExtraSku || !profesionalExtraSku ? (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
              Falta el catálogo de extras. Corré <code>npm run migrate</code> en el API del
              Panel (coofit_core).
            </p>
          ) : (
            <>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ExtraStepper
                label="Consultorios extra"
                included={extrasSnapshot?.includedConsultorios ?? 1}
                value={draftConsultorioExtra}
                used={extrasSnapshot?.usageAvailable ? extrasSnapshot.usedConsultorios : null}
                min={
                  extrasSnapshot?.usageAvailable
                    ? extrasSnapshot.inferredExtraConsultorios
                    : 0
                }
                unitPrice={consultorioExtraSku.precioUnitario}
                disabled={readOnly}
                onChange={setDraftConsultorioExtra}
              />
              <ExtraStepper
                label="Profesionales extra"
                included={extrasSnapshot?.includedProfesionales ?? 1}
                value={draftProfesionalExtra}
                used={extrasSnapshot?.usageAvailable ? extrasSnapshot.usedProfesionales : null}
                min={
                  extrasSnapshot?.usageAvailable
                    ? extrasSnapshot.inferredExtraProfesionales
                    : 0
                }
                unitPrice={profesionalExtraSku.precioUnitario}
                disabled={readOnly}
                onChange={setDraftProfesionalExtra}
              />
            </div>
            {readOnly
            && extrasSnapshot?.usageAvailable
            && (extrasSnapshot.inferredExtraConsultorios > extrasSnapshot.extraConsultorios
              || extrasSnapshot.inferredExtraProfesionales > extrasSnapshot.extraProfesionales) ? (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                La clínica ya tiene {extrasSnapshot.usedConsultorios} consultorio(s) y{' '}
                {extrasSnapshot.usedProfesionales} profesional(es) activos en Coofit. Al Editar se
                alinean los extras a ese uso (cobro en la próxima factura).
              </p>
            ) : null}
            {extrasSnapshot && !extrasSnapshot.usageAvailable ? (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                No se pudo leer consultorios y profesionales de la BD de la clínica. Los extras no
                se infieren hasta que esa conexión esté disponible.
              </p>
            ) : null}
            </>
          )}
        </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <div className="space-y-2 lg:self-start">
          {productBtn(
            'conexion',
            'Coofit Conexión',
            draftConexionOn
              ? draftConexion
                ? `${shortTierLabel('conexion', draftConexion.cupoMensajes)} · ${(
                    draftConexion.cupoMensajes ?? 0
                  ).toLocaleString('es-AR')} msgs/mes`
                : 'Prendido · elegí un escalón'
              : 'Apagado',
            waDirty,
          )}
          {productBtn(
            'marketing',
            'Coofit Marketing',
            draftMarketingOn
              ? draftMarketing
                ? `${shortTierLabel('marketing', draftMarketing.cupoMensajes)} · ${(
                    draftMarketing.cupoMensajes ?? 0
                  ).toLocaleString('es-AR')} emails/mes`
                : 'Prendido · elegí un escalón'
              : 'Apagado',
            mktDirty,
          )}
          {productBtn(
            'bot',
            'Agenda WhatsApp',
            !draftConexionOn
              ? 'Add-on de Conexión · prendé Conexión primero'
              : draftBotOn
                ? draftBot
                  ? `Prendido · ${money(draftBot.precioUnitario)}/mes`
                  : 'Prendido · elegí un pack'
                : 'Apagado · add-on de Conexión',
            botDirty,
          )}
        </div>

        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 dark:border-gray-700">
          <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-3 py-3 dark:border-gray-700">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {product === 'conexion'
                  ? 'Coofit Conexión'
                  : product === 'marketing'
                    ? 'Coofit Marketing'
                    : 'Agenda WhatsApp'}
              </h4>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {product === 'conexion'
                  ? 'WhatsApp + App + confirmaciones por email. El bot de agenda es un add-on.'
                  : product === 'marketing'
                    ? 'Campañas de email marketing.'
                    : 'Add-on de Coofit Conexión. Bot que agenda, reprograma y cancela turnos por chat.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {product === 'bot' ? null : (
                <Link
                  to={product === 'conexion' ? '/comercial/conexion' : '/comercial/marketing'}
                  className="text-xs font-medium text-teal-600 hover:underline dark:text-teal-400"
                >
                  Ver precios
                </Link>
              )}
              <EnableSwitch
                on={productOn}
                disabled={
                  readOnly ||
                  (product === 'bot' && (botCatalog.length === 0 || !draftConexionOn))
                }
                onChange={setProductEnabled}
                onLabel="Prendido"
                offLabel="Apagado"
              />
            </div>
          </div>
          <nav
            className="flex shrink-0 gap-0.5 overflow-x-auto border-b border-slate-200 px-1 dark:border-gray-700"
            aria-label="Detalle comercial"
          >
            <button
              type="button"
              className={subtabClass('escalon')}
              onClick={() => setDetailTab('escalon')}
            >
              {product === 'bot' ? 'Pack' : 'Escalón'}
              {escalonDirty ? (
                <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-300">
                  Pendiente
                </span>
              ) : null}
            </button>
            {product === 'conexion' ? (
              <button
                type="button"
                className={subtabClass('bonos')}
                onClick={() => setDetailTab('bonos')}
              >
                Bonos y excedente
                {waBonosDirty ? (
                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-300">
                    Pendiente
                  </span>
                ) : null}
              </button>
            ) : null}
            {product === 'bot' ? null : (
              <button
                type="button"
                className={subtabClass('trial')}
                onClick={() => setDetailTab('trial')}
              >
                Trial
                {trialDirty ? (
                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-300">
                    Pendiente
                  </span>
                ) : null}
              </button>
            )}
          </nav>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 sm:p-4">
          {product === 'bot' ? (
            !draftConexionOn ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                Agenda WhatsApp es un add-on de Coofit Conexión. Prendé Conexión en la
                columna de la izquierda para poder asignar el bot.
              </p>
            ) : botCatalog.length === 0 ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                No hay ningún pack "Coofit Agenda WhatsApp" activo en el catálogo. Corré la
                migración de coofit_core o creá el pack antes de asignarlo.
              </p>
            ) : (
              <>
                {!draftBotOn ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Coofit Agenda WhatsApp está apagado. El bot no responde los mensajes de los
                    pacientes de esta clínica.
                  </p>
                ) : (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Add-on de Coofit Conexión: usa el mismo número de WhatsApp. Los
                  recordatorios siguen funcionando aparte.
                </p>
                {botCatalog.length === 1 && draftBot ? (
                  <div className="rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {draftBot.nombre}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {money(draftBot.precioUnitario)} / mes
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {botCatalog.map((item) => (
                      <label
                        key={item.id}
                        className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-gray-700"
                      >
                        <input
                          type="radio"
                          name="pack-bot"
                          checked={draftBotId === item.id}
                          disabled={readOnly}
                          onChange={() => {
                            setDraftBotId(item.id);
                            lastBotId.current = item.id;
                          }}
                          className="h-4 w-4 cursor-pointer border-slate-300 text-teal-600 focus:ring-teal-500 disabled:cursor-not-allowed"
                        />
                        <span className="flex-1 text-gray-900 dark:text-white">{item.nombre}</span>
                        <span className="tabular-nums text-slate-600 dark:text-slate-300">
                          {money(item.precioUnitario)} / mes
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {!productReady ? (
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">
                    Elegí un pack para poder guardar.
                  </p>
                ) : null}
              </>
                )}
                {pendingBot ? (
                  <PendingChangeBanner
                    pending={pendingBot}
                    catalog={botCatalog}
                    kind="bot"
                    onCancel={() => cancelPending(pendingBot)}
                    canceling={cancelingChangeId === pendingBot.id}
                  />
                ) : botDirty && savedBotId != null ? (
                  <ForceImmediateControl
                    checked={forceBot}
                    onCheckedChange={setForceBot}
                    reason={forceBotReason}
                    onReasonChange={setForceBotReason}
                  />
                ) : null}
              </>
            )
          ) : null}

          {product === 'conexion' && detailTab === 'escalon' ? (
            draftConexionOn ? (
            <>
              {conexionCatalog.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                  Catálogo sin escalones Conexión.{' '}
                  <Link to="/comercial/conexion" className="font-semibold underline">
                    Configurar en Coofit Conexión
                  </Link>
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Obligatorio: elegí un escalón interno para dejar Conexión prendido.
                  </p>
                  <TierSelect
                    kind="conexion"
                    volumeHeader="Mensajes incluidos / mes"
                    catalog={conexionCatalog}
                    selectedId={draftConexionId}
                    readOnly={readOnly}
                    onSelect={(id) => {
                      setDraftConexionId(id);
                      if (id != null) lastConexionId.current = id;
                    }}
                  />
                  {draftConexionId == null ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-300">
                      Sin escalón no se puede guardar.
                    </p>
                  ) : null}
                </>
              )}

              {pendingConexion ? (
                <PendingChangeBanner
                  pending={pendingConexion}
                  catalog={conexionCatalog}
                  kind="conexion"
                  onCancel={() => cancelPending(pendingConexion)}
                  canceling={cancelingChangeId === pendingConexion.id}
                />
              ) : waEscalonDirty && savedConexionId != null ? (
                <ForceImmediateControl
                  checked={forceConexion}
                  onCheckedChange={setForceConexion}
                  reason={forceConexionReason}
                  onReasonChange={setForceConexionReason}
                />
              ) : null}

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Al alcanzar el límite
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-200">
                  {overage?.enabled
                    ? overage.maxMessages != null
                      ? `Excedentes aceptados, con tope de ${overage.maxMessages.toLocaleString('es-AR')} mensajes extra por mes.`
                      : 'Excedentes aceptados, sin tope mensual.'
                    : 'Bloquea los envíos hasta el próximo mes.'}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Lo decide la clínica desde su configuración de Coofit Conexión. Las tarifas por
                  tramo se administran en Comercial → Coofit Conexión.
                </p>
              </div>
            </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Coofit Conexión está apagado. Prendelo para elegir un escalón obligatorio.
              </p>
            )
          ) : null}

          {product === 'conexion' && detailTab === 'bonos' ? (
            !productReady ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Prendé Coofit Conexión y elegí un escalón para poder recargar.
              </p>
            ) : (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Recarga manual de mensajes de Coofit Conexión ({period}). Suma cupo WhatsApp
                para automáticos y manuales. El motivo queda registrado para auditoría y se
                confirma al guardar.
              </p>
              {!readOnly ? (
                <div className="space-y-2.5 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <input
                      type="checkbox"
                      checked={includeWaRecharge}
                      onChange={(e) => setIncludeWaRecharge(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Agregar recarga de mensajes
                  </label>
                  {includeWaRecharge ? (
                    <>
                      <Input
                        label="Mensajes"
                        type="number"
                        min={1}
                        value={rechargeMessages}
                        onChange={(e) =>
                          setRechargeMessages(parseInt(e.target.value, 10) || 0)
                        }
                      />
                      <Input
                        label="Motivo (auditoría)"
                        type="text"
                        value={rechargeReason}
                        onChange={(e) => setRechargeReason(e.target.value)}
                        placeholder="Ej. compensación por corte, error de cobro, cortesía comercial"
                      />
                      <p className="text-[11px] text-slate-500">
                        Mínimo 5 caracteres. Se guarda como «Recarga manual: …».
                      </p>
                    </>
                  ) : null}
                </div>
              ) : null}
              {waHistory.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Bonos activos este mes
                  </p>
                  <ul className="space-y-1.5">
                    {waHistory.map((b) => (
                      <li
                        key={b.id}
                        className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 dark:border-gray-700 dark:text-slate-300"
                      >
                        <p>
                          +{b.messages.toLocaleString('es-AR')} msgs · {scopeLabel(b.scope)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-500">{b.reason}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">Sin bonos activos este mes.</p>
              )}

              <div className="rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Excedente del mes ({overage?.period ?? period})
                </p>
                {overage && overage.overageMessages > 0 ? (
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                    {overage.overageMessages.toLocaleString('es-AR')} mensajes fuera del plan ·{' '}
                    {money(overage.pricePerMessage)} por mensaje ·{' '}
                    <strong>{money(overage.amount)}</strong>
                    {overage.status === 'billed' ? ' (ya facturado)' : ' (se factura al cierre)'}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Sin excedente este mes.
                  </p>
                )}
              </div>
            </>
            )
          ) : null}

          {product === 'conexion' && detailTab === 'trial' ? (
            !productReady ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Prendé Coofit Conexión y elegí un escalón para poder cargar un trial.
              </p>
            ) : (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Bonificación de {period}. Queda pendiente hasta confirmar al guardar.
              </p>

              {!readOnly ? (
                <div className="space-y-2.5 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <input
                      type="checkbox"
                      checked={includeWaBonus}
                      onChange={(e) => setIncludeWaBonus(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Incluir bonificación / trial Conexión
                  </label>
                  {includeWaBonus ? (
                    <>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setWaMessages(50);
                            setWaScope('manual_only');
                            setWaReason('Trial comercial — envíos manuales');
                          }}
                        >
                          Trial 50 manual
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          label="Mensajes"
                          type="number"
                          min={1}
                          value={waMessages}
                          onChange={(e) => setWaMessages(parseInt(e.target.value, 10) || 0)}
                        />
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Alcance
                          </label>
                          <select
                            value={waScope}
                            onChange={(e) =>
                              setWaScope(e.target.value === 'all' ? 'all' : 'manual_only')
                            }
                            className={fieldClass}
                          >
                            <option value="manual_only">Solo manual</option>
                            <option value="all">Auto + manual</option>
                          </select>
                        </div>
                      </div>
                      <Input
                        label="Motivo"
                        type="text"
                        value={waReason}
                        onChange={(e) => setWaReason(e.target.value)}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}

              {waHistory.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Bonos activos este mes
                  </p>
                  <ul className="space-y-1">
                    {waHistory.map((b) => {
                      const marked = revokeWaIds.includes(b.id);
                      return (
                        <li
                          key={b.id}
                          className="flex items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300"
                        >
                          <span className={marked ? 'line-through opacity-60' : ''}>
                            +{b.messages.toLocaleString('es-AR')} · {scopeLabel(b.scope)}
                          </span>
                          {!readOnly ? (
                            <button
                              type="button"
                              className="text-rose-600 hover:underline"
                              onClick={() =>
                                setRevokeWaIds((ids) =>
                                  ids.includes(b.id)
                                    ? ids.filter((id) => id !== b.id)
                                    : [...ids, b.id],
                                )
                              }
                            >
                              {marked ? 'Conservar' : 'Anular'}
                            </button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">Sin bonos WhatsApp activos este mes.</p>
              )}
            </>
            )
          ) : null}

          {product === 'marketing' && detailTab === 'escalon' ? (
            draftMarketingOn ? (
            <>
              {marketingCatalog.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                  Catálogo sin escalones Marketing.{' '}
                  <Link to="/comercial/marketing" className="font-semibold underline">
                    Configurar en Coofit Marketing
                  </Link>
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Obligatorio: elegí un escalón interno para dejar Marketing prendido.
                  </p>
                  <TierSelect
                    kind="marketing"
                    volumeHeader="Emails incluidos / mes"
                    catalog={marketingCatalog}
                    selectedId={draftMarketingId}
                    readOnly={readOnly}
                    onSelect={(id) => {
                      setDraftMarketingId(id);
                      if (id != null) lastMarketingId.current = id;
                    }}
                  />
                  {draftMarketingId == null ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-300">
                      Sin escalón no se puede guardar.
                    </p>
                  ) : null}
                </>
              )}

              {pendingMarketing ? (
                <PendingChangeBanner
                  pending={pendingMarketing}
                  catalog={marketingCatalog}
                  kind="marketing"
                  onCancel={() => cancelPending(pendingMarketing)}
                  canceling={cancelingChangeId === pendingMarketing.id}
                />
              ) : mktEscalonDirty && savedMarketingId != null ? (
                <ForceImmediateControl
                  checked={forceMarketing}
                  onCheckedChange={setForceMarketing}
                  reason={forceMarketingReason}
                  onReasonChange={setForceMarketingReason}
                />
              ) : null}
            </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Coofit Marketing está apagado. Prendelo para elegir un escalón obligatorio.
              </p>
            )
          ) : null}

          {product === 'marketing' && detailTab === 'trial' ? (
            !productReady ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Prendé Coofit Marketing y elegí un escalón para poder cargar un trial.
              </p>
            ) : (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Bonificación de {period}. Queda pendiente hasta confirmar al guardar.
              </p>
              {!readOnly ? (
                <div className="space-y-2.5 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <input
                      type="checkbox"
                      checked={includeMktBonus}
                      onChange={(e) => setIncludeMktBonus(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Incluir bonificación / trial Marketing
                  </label>
                  {includeMktBonus ? (
                    <>
                      <Input
                        label="Emails"
                        type="number"
                        min={1}
                        value={mktMessages}
                        onChange={(e) => setMktMessages(parseInt(e.target.value, 10) || 0)}
                      />
                      <Input
                        label="Motivo"
                        type="text"
                        value={mktReason}
                        onChange={(e) => setMktReason(e.target.value)}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}

              {mktHistory.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Bonos activos este mes
                  </p>
                  <ul className="space-y-1">
                    {mktHistory.map((b) => {
                      const marked = revokeMktIds.includes(b.id);
                      return (
                        <li
                          key={b.id}
                          className="flex items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300"
                        >
                          <span className={marked ? 'line-through opacity-60' : ''}>
                            +{b.messages.toLocaleString('es-AR')} emails
                          </span>
                          {!readOnly ? (
                            <button
                              type="button"
                              className="text-rose-600 hover:underline"
                              onClick={() =>
                                setRevokeMktIds((ids) =>
                                  ids.includes(b.id)
                                    ? ids.filter((id) => id !== b.id)
                                    : [...ids, b.id],
                                )
                              }
                            >
                              {marked ? 'Conservar' : 'Anular'}
                            </button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">Sin bonos Marketing activos este mes.</p>
              )}
            </>
            )
          ) : null}
          </div>
        </div>
      </div>
      </div>
    );
  },
);
